import dayjs from 'dayjs'
import weekOfYear from 'dayjs/plugin/weekOfYear'
import isoWeek from 'dayjs/plugin/isoWeek'

dayjs.extend(weekOfYear)
dayjs.extend(isoWeek)

/**
 * 对比场景下缩短 Campaign 显示名，避免表格/图例过长。
 * "(对比时段)" 缩写为 "(对比)"，过长名称截断并加 "…"。
 * @param {string} name - 完整 campaign 名称（如 "Campaign B (对比时段)"）
 * @param {number} maxLen - 目标最大字符数（含后缀），默认 10
 * @returns {string}
 */
export function shortCampaignLabel(name, maxLen = 10) {
  if (!name || typeof name !== 'string') return ''
  const isComparePeriod = name.includes('(对比时段)')
  const base = isComparePeriod ? name.replace(/\s*\(对比时段\)\s*$/, '').trim() : name
  const suffix = isComparePeriod ? '(对比)' : ''
  const maxBase = maxLen - suffix.length
  if (maxBase <= 0) return suffix
  if (base.length <= maxBase) return base + suffix
  return base.slice(0, maxBase - 1) + '…' + suffix
}

/**
 * 计算预测收入
 * 根据用户定义的每日预算和去年同期的 ROAS 曲线来预测未来的每日收入
 *
 * @param {string} predictionStartDate - 预测开始日期 (YYYY-MM-DD)
 * @param {string} predictionEndDate - 预测结束日期 (YYYY-MM-DD)
 * @param {Object} dailyBudgets - 每日预算 { "2026-03-01": 100, ... }
 * @param {Array} historicalData - 历史 dailyData 数组 (来自 ROAS API)
 * @param {number} maxLookbackDays - 最大回溯天数 (默认 90，即考虑 D0-D90 的收入贡献)
 * @returns {Array} - 预测结果，格式兼容 pivotRevenueMixData 的输入
 */
export const calculatePredictedRevenue = (
  predictionStartDate,
  predictionEndDate,
  dailyBudgets,
  historicalData,
  maxLookbackDays = 90,
) => {
  if (!predictionStartDate || !predictionEndDate || !dailyBudgets || !historicalData) {
    return []
  }

  // 1. 构建历史数据查找表：key = install_date, value = days 对象 (包含 day0, day1, ... 的 ROAS)
  const historicalRoasMap = new Map()
  // 同时计算全年平均 ROAS 曲线作为兜底
  const avgRoasAccumulator = {} // { day0: { sum: 0, count: 0 }, ... }
  // 计算月度平均 ROAS 曲线作为缺失兜底
  const monthlyRoasAccumulator = {} // { '2025-01': { cost: 0, days: { day0: 0, ... } } }

  historicalData.forEach((item) => {
    if (item.install_date && item.days) {
      // 计算每日的 ROAS (收入/成本)
      const cost = Number(item.cost) || 0
      if (cost > 0) {
        const roasByDay = {}
        const monthKey = dayjs(item.install_date).format('YYYY-MM')
        if (!monthlyRoasAccumulator[monthKey]) {
          monthlyRoasAccumulator[monthKey] = { cost: 0, days: {} }
        }
        monthlyRoasAccumulator[monthKey].cost += cost
        Object.keys(item.days).forEach((dayKey) => {
          const revenue = Number(item.days[dayKey]) || 0
          const roas = revenue / cost
          roasByDay[dayKey] = roas // 转换为 ROAS 比率

          // 累加到平均值计算
          if (!avgRoasAccumulator[dayKey]) {
            avgRoasAccumulator[dayKey] = { sum: 0, count: 0 }
          }
          avgRoasAccumulator[dayKey].sum += roas
          avgRoasAccumulator[dayKey].count += 1

          // 累加到月度平均值计算（用收入总和与月度成本做加权）
          if (monthlyRoasAccumulator[monthKey].days[dayKey] === undefined) {
            monthlyRoasAccumulator[monthKey].days[dayKey] = 0
          }
          monthlyRoasAccumulator[monthKey].days[dayKey] += revenue
        })
        historicalRoasMap.set(item.install_date, roasByDay)
      }
    }
  })

  // 计算全年平均 ROAS 曲线
  const avgRoas = {}
  Object.keys(avgRoasAccumulator).forEach((dayKey) => {
    const { sum, count } = avgRoasAccumulator[dayKey]
    if (count > 0) {
      avgRoas[dayKey] = sum / count
    }
  })

  // 计算月度平均 ROAS 曲线
  const monthlyRoasMap = new Map()
  Object.keys(monthlyRoasAccumulator).forEach((monthKey) => {
    const { cost, days } = monthlyRoasAccumulator[monthKey]
    if (!cost || cost <= 0) return
    const roasByDay = {}
    Object.keys(days).forEach((dayKey) => {
      roasByDay[dayKey] = days[dayKey] / cost
    })
    monthlyRoasMap.set(monthKey, roasByDay)
  })

  // 2. 生成预测范围内的所有日期
  const start = dayjs(predictionStartDate)
  const end = dayjs(predictionEndDate)
  const predictionDates = []
  let current = start
  while (current.isBefore(end) || current.isSame(end, 'day')) {
    predictionDates.push(current.format('YYYY-MM-DD'))
    current = current.add(1, 'day')
  }

  // 3. 构建预测结果：模拟 dailyData 结构
  // 每个预测的投放日期会产生一条记录，包含 install_date 和 days 对象
  const predictedDailyData = []

  const getMaxDay = (roas) => {
    if (!roas) return -1
    const dayKeys = Object.keys(roas).filter((k) => k.startsWith('day'))
    if (dayKeys.length === 0) return -1
    const maxKey = Math.max(
      ...dayKeys
        .map((k) => parseInt(k.replace('day', ''), 10))
        .filter((n) => !isNaN(n)),
    )
    return isNaN(maxKey) ? -1 : maxKey
  }

  // 遍历预测范围内的每一天作为"投放日期"
  predictionDates.forEach((spendDate) => {
    const budget = Number(dailyBudgets[spendDate]) || 0
    if (budget <= 0) return // 如果该日没有预算，跳过

    // 找到去年同期的参考日期
    const referenceDate = dayjs(spendDate).subtract(1, 'year').format('YYYY-MM-DD')
    let historicalRoas = historicalRoasMap.get(referenceDate)

    // 检查 historicalRoas 是否有效
    // 条件：day0 不为 0 且至少有 10 个以上的 dayN 数据（确保数据完整性）
    const isValidRoas = (roas) => {
      if (!roas || roas.day0 === 0) return false
      const dayKeys = Object.keys(roas).filter((k) => k.startsWith('day'))
      return dayKeys.length >= 10
    }

    if (!isValidRoas(historicalRoas)) {
      // 如果没有有效历史数据，先使用同月平均 ROAS 曲线兜底
      const monthKey = dayjs(referenceDate).format('YYYY-MM')
      let fallbackRoas = monthlyRoasMap.get(monthKey)
      let sourceType = 'monthly'

      // 如果同月平均也无效，使用全年平均 ROAS 曲线兜底
      if (!isValidRoas(fallbackRoas)) {
        if (Object.keys(avgRoas).length === 0) return // 完全没有历史数据，跳过
        fallbackRoas = avgRoas
        sourceType = 'yearly'
      }

      // 使用 fallback 数据（尽量覆盖可用天数）
      const days = {}
      const maxDay = Math.max(maxLookbackDays, getMaxDay(fallbackRoas))
      for (let k = 0; k <= maxDay; k++) {
        const dayKey = `day${k}`
        if (fallbackRoas[dayKey] !== undefined) {
          days[dayKey] = budget * fallbackRoas[dayKey]
        }
      }
      if (Object.keys(days).length > 0) {
        predictedDailyData.push({
          install_date: spendDate,
          days,
          cost: budget,
          isPredicted: true,
          sourceType,
        })
      }
      return
    }

    // 使用历史 ROAS 计算预测收入（尽量覆盖可用天数）
    const days = {}
    const maxDay = Math.max(maxLookbackDays, getMaxDay(historicalRoas))
    for (let k = 0; k <= maxDay; k++) {
      const dayKey = `day${k}`
      if (historicalRoas[dayKey] !== undefined) {
        days[dayKey] = budget * historicalRoas[dayKey]
      }
    }

    if (Object.keys(days).length > 0) {
      predictedDailyData.push({
        install_date: spendDate,
        days,
        cost: budget,
        isPredicted: true,
        sourceType: 'exact',
      })
    }
  })

  return predictedDailyData
}

/**
 * 根据月度日均预算目标生成每日预算
 *
 * @param {Object} monthlyDailyBudgets - 月度日均预算 { "2026-01": 100, "2026-02": 90, ... }
 * @param {string} startDate - 开始日期 (YYYY-MM-DD)
 * @param {string} endDate - 结束日期 (YYYY-MM-DD)
 * @param {number} globalDailyBase - 全局日均基础预算 (用于未设置月度预算的月份)
 * @returns {Object} - 每日预算 { "2026-01-01": 100, ... }
 */
export const generateDailyBudgetsFromMonthly = (
  monthlyDailyBudgets,
  startDate,
  endDate,
  globalDailyBase = 100,
) => {
  const dailyBudgets = {}
  const start = dayjs(startDate)
  const end = dayjs(endDate)

  let current = start
  while (current.isBefore(end) || current.isSame(end, 'day')) {
    const dateStr = current.format('YYYY-MM-DD')
    const monthKey = current.format('YYYY-MM')

    // 检查该月是否有设置月度日均预算
    if (monthlyDailyBudgets[monthKey] !== undefined) {
      // 直接使用月度日均预算
      dailyBudgets[dateStr] = monthlyDailyBudgets[monthKey]
    } else {
      // 使用全局日均基础预算
      dailyBudgets[dateStr] = globalDailyBase
    }

    current = current.add(1, 'day')
  }

  return dailyBudgets
}

/**
 * 计算去年同期的历史消耗数据（按月汇总）
 * 用于在预算规划器中显示参考数据
 *
 * @param {Array} historicalData - 历史 dailyData 数组
 * @param {string} startDate - 预测开始日期 (YYYY-MM-DD)
 * @param {string} endDate - 预测结束日期 (YYYY-MM-DD)
 * @returns {Object} - 去年同期月度消耗 { "2025-01": { total: 3100, days: 31, daily: 100 }, ... }
 */
export const getLastYearMonthlyCost = (historicalData, startDate, endDate) => {
  if (!historicalData || !startDate || !endDate) return {}

  const start = dayjs(startDate).subtract(1, 'year')
  const end = dayjs(endDate).subtract(1, 'year')

  const monthlyData = {}

  historicalData.forEach((item) => {
    const itemDate = dayjs(item.install_date)
    // 检查是否在去年同期范围内
    if (itemDate.isBefore(start) || itemDate.isAfter(end)) return

    const monthKey = itemDate.format('YYYY-MM')
    const cost = Number(item.cost) || 0

    if (!monthlyData[monthKey]) {
      monthlyData[monthKey] = { total: 0, days: 0 }
    }
    monthlyData[monthKey].total += cost
    monthlyData[monthKey].days += 1
  })

  // 计算日均
  Object.keys(monthlyData).forEach((monthKey) => {
    const data = monthlyData[monthKey]
    data.daily = data.days > 0 ? data.total / data.days : 0
  })

  return monthlyData
}

// 计算指定年份的平均 ROAS 曲线（成本加权）
export const buildAvgRoasByYear = (historicalData, year) => {
  if (!historicalData || !year) return { roasByDay: {}, maxDay: -1 }
  const totalRevenueByDay = {}
  let totalCost = 0

  historicalData.forEach((item) => {
    if (!item.install_date || !item.days) return
    const itemYear = dayjs(item.install_date).year()
    if (itemYear !== year) return
    const cost = Number(item.cost) || 0
    if (cost <= 0) return
    totalCost += cost
    Object.keys(item.days).forEach((dayKey) => {
      const revenue = Number(item.days[dayKey]) || 0
      totalRevenueByDay[dayKey] = (totalRevenueByDay[dayKey] || 0) + revenue
    })
  })

  if (totalCost <= 0) return { roasByDay: {}, maxDay: -1 }

  const roasByDay = {}
  let maxDay = -1
  Object.keys(totalRevenueByDay).forEach((dayKey) => {
    roasByDay[dayKey] = totalRevenueByDay[dayKey] / totalCost
    const dayN = parseInt(dayKey.replace('day', ''), 10)
    if (!isNaN(dayN)) {
      maxDay = Math.max(maxDay, dayN)
    }
  })

  return { roasByDay, maxDay }
}

export const aggregateDailyData = (dailyData, summaryMode) => {
  if (!dailyData || dailyData.length === 0 || summaryMode === 'daily') {
    return dailyData
  }

  // Helper to sum up nested objects like days.day0, days.day1...
  const sumObject = (target, source) => {
    if (!source) return
    Object.keys(source).forEach((key) => {
      if (typeof source[key] === 'number') {
        target[key] = (target[key] || 0) + source[key]
      }
    })
  }

  // Helper to track applicable costs for each day to avoid ROAS distortion
  const trackDayCosts = (targetCosts, sourceDays, cost) => {
    if (!sourceDays || !cost) return
    Object.keys(sourceDays).forEach((key) => {
      if (sourceDays[key] !== undefined) {
        targetCosts[key] = (targetCosts[key] || 0) + cost
      }
    })
  }

  // Helper to accumulate valid cumulative revenues
  const accumulateValidRevenue = (targetCum, sourceDays, cost) => {
    if (!sourceDays || !cost) return
    let runningTotal = 0
    // We assume keys are day0, day1... dayN. 
    // We should iterate up to maxDay (e.g. 180) or find max key in sourceDays.
    // Finding max key is safer.
    const keys = Object.keys(sourceDays)
    const maxKey = Math.max(...keys.map(k => parseInt(k.replace('day', ''), 10)).filter(n => !isNaN(n)))
    
    for (let i = 0; i <= maxKey; i++) {
        const key = `day${i}`
        const val = sourceDays[key] || 0
        runningTotal += val
        
        // If the key exists in sourceDays, it means this day is valid for this item (it has reached this day)
        // So we add its running total to the group's cumulative revenue for this day
        if (sourceDays[key] !== undefined) {
            targetCum[key] = (targetCum[key] || 0) + runningTotal
        }
    }
  }

  // Helper to count valid cohorts for each day
  const trackDayCounts = (targetCounts, sourceDays) => {
    if (!sourceDays) return
    Object.keys(sourceDays).forEach((key) => {
      if (sourceDays[key] !== undefined) {
        targetCounts[key] = (targetCounts[key] || 0) + 1
      }
    })
  }

  const grouped = {}

  dailyData.forEach((item) => {
    let key, displayDate
    const date = dayjs(item.install_date)

    if (summaryMode === 'weekly') {
      // Group by start of the week (Sunday based on WeekSelect)
      const startOfWeek = date.startOf('week')
      key = startOfWeek.format('YYYY-MM-DD')
      const endOfWeek = startOfWeek.add(6, 'day')
      displayDate = `${startOfWeek.format('MM/DD')} - ${endOfWeek.format('MM/DD')}`
    } else if (summaryMode === 'monthly') {
      key = date.startOf('month').format('YYYY-MM-DD')
      displayDate = date.format('YYYY-MM')
    } else {
      return // Should not happen given initial check
    }

    if (!grouped[key]) {
      grouped[key] = {
        install_date: key,
        displayDate: displayDate,
        installs: 0,
        activeInstalls: 0,
        revenue: 0,
        cost: 0,
        total_revenue: 0,
        total_cost: 0,
        days: {},
        predictRevenueDays: {},
        predictRevenueAccDays: {},
        iaa: {},
        iap: {},
        installDays: {},
        activeDays: {},
        impressionDays: {},
        dayCosts: {}, // Track cost of cohorts that reached each day
        dayCounts: {}, // Track number of cohorts that reached each day
        predictDayCosts: {}, // Track cost for prediction data
        cumulativeRevenueDays: {}, // Track valid cumulative revenue for each day
        cumulativePredictRevenueDays: {}, // Track valid cumulative prediction revenue
        cumulativeIaaDays: {},
        cumulativeIapDays: {},
        allModelsData: new Map(),
        allModelsDayCosts: new Map(), // Track costs for each model
        allModelsCumulativeData: new Map(), // Track cumulative revenue for each model
        // 官方 roasAcc 为倍率，周/月汇总时用 cost 加权平均（不可像收入一样直接相加）
        _predRoasNum: {},
        _predRoasDenom: {},
        // Note: index, rowId will be regenerated by the table
      }
    }

    const group = grouped[key]
    const itemCost = Number(item.cost) || 0

    // Sum basic metrics
    group.installs += (Number(item.installs) || 0)
    group.activeInstalls += (Number(item.activeInstalls) || 0)
    group.revenue += (Number(item.revenue) || 0)
    group.cost += itemCost
    group.total_revenue += (Number(item.total_revenue) || 0)
    group.total_cost += (Number(item.total_cost) || 0)

    // Sum nested objects (Standard daily sum)
    sumObject(group.days, item.days)
    sumObject(group.predictRevenueDays, item.predictRevenueDays)
    sumObject(group.predictRevenueAccDays, item.predictRevenueAccDays)
    sumObject(group.iaa, item.iaa)
    sumObject(group.iap, item.iap)
    sumObject(group.installDays, item.installDays)
    sumObject(group.activeDays, item.activeDays)
    sumObject(group.impressionDays, item.impressionDays)
    
    // Track applicable costs
    trackDayCosts(group.dayCosts, item.days, itemCost)
    trackDayCosts(group.predictDayCosts, item.predictRevenueDays, itemCost)

    // Track valid cohorts count
    trackDayCounts(group.dayCounts, item.days)

    // Accumulate Valid Cumulative Revenues
    accumulateValidRevenue(group.cumulativeRevenueDays, item.days, itemCost)
    accumulateValidRevenue(group.cumulativePredictRevenueDays, item.predictRevenueDays, itemCost)
    accumulateValidRevenue(group.cumulativeIaaDays, item.iaa, itemCost)
    accumulateValidRevenue(group.cumulativeIapDays, item.iap, itemCost)

    // 预测 ROAS（roasAcc）：按 cohort cost 加权累加，最后在 map 阶段除成加权平均
    if (item.predictRoasAccDays && typeof item.predictRoasAccDays === 'object') {
      const w = itemCost > 0 ? itemCost : 1
      Object.keys(item.predictRoasAccDays).forEach((dk) => {
        const raw = item.predictRoasAccDays[dk]
        if (raw == null || raw === '') return
        const v = Number(raw)
        if (!Number.isFinite(v)) return
        group._predRoasNum[dk] = (group._predRoasNum[dk] || 0) + v * w
        group._predRoasDenom[dk] = (group._predRoasDenom[dk] || 0) + w
      })
    }

    // For allModelsData, it's a Map<modelName, daysObj>.
    if (item.allModelsData) {
        item.allModelsData.forEach((modelData, modelName) => {
            if (!group.allModelsData.has(modelName)) {
                group.allModelsData.set(modelName, {})
                group.allModelsDayCosts.set(modelName, {})
                group.allModelsCumulativeData.set(modelName, {})
            }
            const targetModelData = group.allModelsData.get(modelName)
            sumObject(targetModelData, modelData)

            const targetModelCosts = group.allModelsDayCosts.get(modelName)
            trackDayCosts(targetModelCosts, modelData, itemCost)
            
            const targetModelCum = group.allModelsCumulativeData.get(modelName)
            accumulateValidRevenue(targetModelCum, modelData, itemCost)
        })
    }
  })

  // Convert to array and post-process (calculate ROAS, etc.)
  const result = Object.values(grouped).map((group) => {
    // Filter out days with less than 7 cohorts in summary mode
    if (summaryMode !== 'daily') {
        Object.keys(group.dayCounts).forEach(dayKey => {
            if (group.dayCounts[dayKey] < 7) {
                // Remove actual data for this day
                delete group.days[dayKey]
                delete group.dayCosts[dayKey]
                delete group.cumulativeRevenueDays[dayKey]
                delete group.cumulativeIaaDays[dayKey]
                delete group.cumulativeIapDays[dayKey]
                // Note: We keep prediction data as it's usually projected
            }
        })
    }

    // Recalculate summary ROAS (Revenue / Cost)
    const roas = group.cost > 0 ? (group.revenue / group.cost) * 100 : 0

    const predictRoasAccDays = {}
    if (group._predRoasNum && typeof group._predRoasNum === 'object') {
      Object.keys(group._predRoasNum).forEach((dk) => {
        const den = group._predRoasDenom[dk]
        if (den > 0) predictRoasAccDays[dk] = group._predRoasNum[dk] / den
      })
    }

    const groupRest = { ...group }
    delete groupRest._predRoasNum
    delete groupRest._predRoasDenom
    
    return {
      ...groupRest,
      predictRoasAccDays,
      roas: roas,
      // For Day 90 ROAS, use applicable cost if available
      total_d90_roas: (group.cumulativeRevenueDays?.day90 !== undefined) 
        ? (group.cumulativeRevenueDays.day90 / (group.dayCosts?.day90 || group.cost)) * 100 
        : undefined
    }
  })

  // Sort by date descending
  return result.sort((a, b) => b.install_date.localeCompare(a.install_date))
}

export const getCohortRange = (dayN, simplified = false) => {
  if (!simplified) return `D${dayN}`
  
  if (dayN <= 7) return `D${dayN}`
  if (dayN <= 14) return 'D8-14'
  if (dayN <= 30) return 'D15-30'
  if (dayN <= 60) return 'D31-60'
  if (dayN <= 90) return 'D61-90'
  return 'D91+'
}


export const pivotRevenueMixData = (dailyData, simplified = false, summaryMode = 'daily') => {
  if (!dailyData || dailyData.length === 0) return []

  const mixMap = {}
  
  // 1. 先遍历一遍处理 Cost，确保每一天（或周/月）的 Cost 都被记录
  // 同时这也能初始化所有有 Cost 的日期条目
  dailyData.forEach((item) => {
    const installDate = item.install_date
    const cost = Number(item.cost) || 0
    const baseDate = dayjs(installDate)
    
    let rDate = baseDate
    let displayDate = ''
    let revenueDate = ''
    
    // 日期归一化逻辑
    if (summaryMode === 'weekly') {
      const startOfWeek = rDate.startOf('week')
      const endOfWeek = startOfWeek.add(6, 'day')
      displayDate = `${startOfWeek.format('YYYY-MM-DD')} - ${endOfWeek.format('YYYY-MM-DD')}`
      revenueDate = startOfWeek.format('YYYY-MM-DD')
    } else if (summaryMode === 'monthly') {
      const startOfMonth = rDate.startOf('month')
      displayDate = startOfMonth.format('YYYY-MM')
      revenueDate = startOfMonth.format('YYYY-MM-DD')
    } else {
      const daysOfWeek = ['日', '一', '二', '三', '四', '五', '六']
      const dayOfWeek = daysOfWeek[rDate.day()]
      displayDate = `${rDate.format('YYYY-MM-DD')} (周${dayOfWeek})`
      revenueDate = rDate.format('YYYY-MM-DD')
    }

    if (!mixMap[revenueDate]) {
      mixMap[revenueDate] = {
        revenue_date: revenueDate,
        display_date: displayDate,
        total: 0,
        cost: 0,
        profit: 0,
        roas: 0,
        er: 0,
        cohorts: {},
        ranges: {},
        rowId: `mix_${revenueDate}`,
      }
    }
    
    // 累加 Cost
    mixMap[revenueDate].cost += cost
  })

  // 2. 再遍历一遍处理 Revenue Mix
  dailyData.forEach((item) => {
    const installDate = item.install_date
    const days = item.days || {}
    const baseDate = dayjs(installDate)

    Object.keys(days).forEach((dayKey) => {
      const dayN = parseInt(dayKey.replace('day', ''), 10)
      if (isNaN(dayN)) return

      const revenue = Number(days[dayKey]) || 0
      if (revenue === 0) return

      let rDate = baseDate.add(dayN, 'day')
      let displayDate = ''
      let revenueDate = ''
      
      // 使用相同的日期归一化逻辑
      if (summaryMode === 'weekly') {
        const startOfWeek = rDate.startOf('week')
        const endOfWeek = startOfWeek.add(6, 'day')
        displayDate = `${startOfWeek.format('YYYY-MM-DD')} - ${endOfWeek.format('YYYY-MM-DD')}`
        revenueDate = startOfWeek.format('YYYY-MM-DD')
      } else if (summaryMode === 'monthly') {
        const startOfMonth = rDate.startOf('month')
        displayDate = startOfMonth.format('YYYY-MM')
        revenueDate = startOfMonth.format('YYYY-MM-DD')
      } else {
        const daysOfWeek = ['日', '一', '二', '三', '四', '五', '六']
        const dayOfWeek = daysOfWeek[rDate.day()]
        displayDate = `${rDate.format('YYYY-MM-DD')} (周${dayOfWeek})`
        revenueDate = rDate.format('YYYY-MM-DD')
      }

      if (!mixMap[revenueDate]) {
        mixMap[revenueDate] = {
          revenue_date: revenueDate,
          display_date: displayDate,
          total: 0,
          cost: 0, // 如果这天没有 Cost 记录（比如只作为 Revenue Date 出现），Cost 为 0
          profit: 0,
          roas: 0,
          er: 0,
          cohorts: {},
          ranges: {},
          rowId: `mix_${revenueDate}`,
        }
      }

      const entry = mixMap[revenueDate]
      entry.total += revenue
      entry.cohorts[dayKey] = (entry.cohorts[dayKey] || 0) + revenue

      const range = getCohortRange(dayN, simplified)
      entry.ranges[range] = (entry.ranges[range] || 0) + revenue
    })
  })

  // 3. 计算衍生指标
  return Object.values(mixMap).map(entry => {
    entry.profit = entry.total - entry.cost
    entry.roas = entry.cost > 0 ? (entry.total / entry.cost) * 100 : 0
    entry.er = entry.total > 0 ? (entry.profit / entry.total) * 100 : 0
    return entry
  }).sort((a, b) =>
    b.revenue_date.localeCompare(a.revenue_date),
  )
}

